import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import FormField from '../../components/ui/FormField.jsx';
import SumCaptcha from '../../components/auth/SumCaptcha.jsx';
import AdminAuthShell, { adminAuthInputLight, AdminAuthBackLink } from '../../components/admin/AdminAuthShell.jsx';
import api from '../../services/api.js';
import { toastError, toastSuccess } from '../../hooks/useToast.js';

export default function ForgotPasswordPage() {
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { email: '' },
  });

  async function onSubmit(values) {
    if (!captchaValid) {
      toastError('Verificación requerida', 'Selecciona el resultado correcto de la suma.');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post('/v1/auth/forgot-password', { email: values.email });
      if (!data.success) throw new Error(data.message || 'No se pudo procesar la solicitud');
      setSent(true);
      toastSuccess('Solicitud enviada', data.message);
    } catch (e) {
      toastError('No se pudo enviar', e.message);
      setCaptchaAnswer('');
      setCaptchaValid(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AdminAuthShell
      title="Recuperar contraseña"
      subtitle="Le enviaremos un enlace para restablecer su acceso al panel"
      footer={<AdminAuthBackLink />}
    >
      {sent ? (
        <div className="space-y-4 text-sm leading-relaxed text-slate-600">
          <p>
            Si el correo está registrado, recibirá un enlace para restablecer su contraseña. Revise también la
            carpeta de spam.
          </p>
          <p>El enlace expira en 1 hora por seguridad.</p>
        </div>
      ) : (
        <form
          className="space-y-5 [&_label>span]:text-slate-600 [&_p.text-red-400]:text-red-600"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <p className="text-sm text-slate-600">
            Ingrese el correo asociado a su cuenta administrativa.
          </p>

          <FormField label="Correo" error={errors.email?.message}>
            <input
              type="email"
              autoComplete="email"
              placeholder="Correo electrónico"
              className={adminAuthInputLight}
              {...register('email', {
                required: 'Requerido',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Correo inválido',
                },
              })}
            />
          </FormField>

          <SumCaptcha
            value={captchaAnswer}
            onChange={setCaptchaAnswer}
            onValidChange={setCaptchaValid}
          />

          <Button type="submit" disabled={submitting || !captchaValid} className="w-full">
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Enviando…
              </>
            ) : (
              'Enviar enlace de recuperación'
            )}
          </Button>
        </form>
      )}
    </AdminAuthShell>
  );
}
